import type { TextFont } from "common/constants/textFont";
import { FontId, TextFonts } from "common/constants/textFont";
import { notifyFontSet } from "./fontCallbacks";
import { fonts } from "./planFonts";

export const DefaultFonts: TextFont[] = [
  TextFonts.Arial,
  TextFonts.ArialNarrow,
  TextFonts.ArialNarrow_Bold,
  TextFonts.ComingSoon,
  TextFonts.CourierPrime,
  TextFonts.OpenSans,
  TextFonts.PTSansNarrow,
  TextFonts.SourceSerif,
  { id: FontId.Helvetica, path: "/static/fonts/Helvetica.afm" },
] as const;

export const BrochureFonts: TextFont[] = [
  ...DefaultFonts,
  TextFonts.Circular,
  TextFonts.Circular_Bold,
  TextFonts.Circular_Book,
  TextFonts.Circular_Medium,
] as const;

let helveticaPromise: Promise<void>;
export async function loadHelvetica() {
  // pdfkit requires Helvetica to be on the virtual file system loaded by our
  // system when using pdfkit in the browser.

  if (typeof process === "object" && process.env?.JEST_WORKER_ID !== undefined) {
    // jest makes things extra annoying because it automagically injects jsdom
    return;
  }

  // in browser or worker?
  if (typeof window === "object" || typeof self === "object") {
    if (helveticaPromise) {
      return helveticaPromise;
    }

    return (helveticaPromise = new Promise(async (resolve) => {
      const response = await fetch("/static/fonts/Helvetica.afm");

      if (!response.ok) {
        throw new Error(`Failed to fetch font: ${response.status}`);
      }

      const buff = await response.text();

      const fs = await import("fs");
      fs.default.writeFileSync("data/Helvetica.afm", buff);

      resolve();
    }));
  }
}

export type FontSources = { [fontId: string]: string };

const loadedFonts: { [fontId: string]: ArrayBuffer } = {};

export const loadFonts = async (fonts: TextFont[] = DefaultFonts, absoluteURLPrefix?: string) => {
  await loadHelvetica();
  const isServer = typeof window !== "object" && typeof self !== "object";
  return Promise.all(
    fonts
      .filter((font) => !loadedFonts[font.id])
      .map(async (font) => {
        let buffer: ArrayBuffer;
        if (isServer) {
          const { readFileSync } = await import("fs");
          const path = await import("path");
          const staticRoot = path.resolve(process.cwd(), "apps/web/public");
          const filePath = path.join(staticRoot, font.path.replace(/^\//, ""));
          const fileBuffer = readFileSync(filePath);
          buffer = fileBuffer.buffer.slice(fileBuffer.byteOffset, fileBuffer.byteOffset + fileBuffer.byteLength);
        } else {
          const response = await fetch(absoluteURLPrefix ? absoluteURLPrefix + font.path : font.path);
          if (!response.ok) {
            throw new Error(`Failed to fetch font: ${response.status}`);
          }
          buffer = await response.arrayBuffer();
        }
        setFont(font.id, buffer);
      }),
  );
};

export function registerFontsWithPDF(pdf: PDFKit.PDFDocument) {
  Object.keys(loadedFonts).forEach((fontId) => {
    pdf.registerFont(fontId, getFont(fontId) as any);
  });
}

export const getFont = (fontId: string) => loadedFonts[fontId];
export const setFont = (fontId: string, buffer: ArrayBuffer) => {
  loadedFonts[fontId] = buffer;
  notifyFontSet(fontId);
};

// for SVGtoPdfkit lib
export function fontCallback(_family: string, bold: boolean) {
  if (bold) {
    return getFont(fonts.planSetBold);
  }
  return getFont(fonts.planSetRegular);
}
